/**
 * @file AiLogDrawer.tsx
 * @description AI 生成日誌查看抽屜：列出本案每次 LLM 板塊調用的輸入上下文與輸出。
 *
 * 供稽核員 / 主管核對 AI 是否依真實資料生成、有無編造，以及 fallback / error 情形。
 */
import { useQuery } from '@tanstack/react-query';
import type { AiGenerationLogDto } from '@zhcb/shared';
import { Collapse, Drawer, Empty, Spin, Tag, Typography } from 'antd';
import { apiClient } from '@/lib/api-client';

/** AiLogDrawer Props */
interface AiLogDrawerProps {
  /** 所屬案件 ID，用於構建 API 請求路徑 */
  caseId: number;
  /** 抽屜是否顯示 */
  open: boolean;
  /** 關閉回呼 */
  onClose: () => void;
}

/** AI 生成狀態對應的 Tag 顏色 */
const STATUS_COLOR: Record<string, string> = {
  llm: 'green',
  fallback: 'orange',
  error: 'red',
};

/**
 * AI 生成日誌查看抽屜。
 *
 * - 僅在 open=true 時觸發 API 請求（enabled: open）。
 * - 每條日誌可展開查看：錯誤訊息、輸入上下文（userPrompt）、輸出結果（output）、System Prompt。
 */
export function AiLogDrawer({ caseId, open, onClose }: AiLogDrawerProps) {
  /** 拉取本案 AI 生成日誌列表 */
  const { data, isLoading } = useQuery({
    queryKey: ['ai-logs', caseId],
    queryFn: async () => {
      // apiClient.get<T> 會自動解包 ApiSuccess.data，直接回傳 T
      const list = await apiClient.get<AiGenerationLogDto[]>(
        `/api/cases/${caseId}/audit-report/ai-logs`,
      );
      return list ?? [];
    },
    // 僅在抽屜打開時才發請求，避免頁面載入時無謂調用
    enabled: open,
  });

  return (
    <Drawer
      title="AI 生成日誌"
      width={720}
      open={open}
      onClose={onClose}
      // antd 6 使用 destroyOnHidden（v5 的 destroyOnClose 已更名）
      destroyOnHidden
    >
      {isLoading ? (
        /* 加載中：居中 Spin */
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !data || data.length === 0 ? (
        /* 無日誌：空狀態提示 */
        <Empty description="尚無 AI 生成日誌" />
      ) : (
        /* 日誌列表：Collapse 折疊面板，每條日誌一個 Panel */
        <Collapse
          items={data.map((logItem) => ({
            key: String(logItem.id),
            label: (
              <span>
                {/* 狀態 Tag：llm=綠、fallback=橙、error=紅 */}
                <Tag color={STATUS_COLOR[logItem.status] ?? 'default'}>{logItem.status}</Tag>

                {/* 板塊 key 或場景名稱 */}
                {logItem.blockKey ?? logItem.scene}

                {/* 模型名稱（若有） */}
                {logItem.model ? (
                  <Typography.Text type="secondary">（{logItem.model}）</Typography.Text>
                ) : null}

                {/* 耗時（若有） */}
                {logItem.latencyMs != null ? (
                  <Typography.Text type="secondary"> · {logItem.latencyMs}ms</Typography.Text>
                ) : null}

                {/* 生成時間 */}
                <Typography.Text type="secondary">
                  {' · '}
                  {new Date(logItem.createdAt).toLocaleString()}
                </Typography.Text>
              </span>
            ),
            children: (
              <div>
                {/* 錯誤訊息（status==='error' 時顯示） */}
                {logItem.errorMessage ? (
                  <Typography.Paragraph type="danger">
                    錯誤：{logItem.errorMessage}
                  </Typography.Paragraph>
                ) : null}

                {/* 輸入上下文（User Prompt） */}
                <Typography.Title level={5}>輸入上下文（User Prompt）</Typography.Title>
                <Typography.Paragraph>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {logItem.userPrompt ?? '（無）'}
                  </pre>
                </Typography.Paragraph>

                {/* 輸出結果 */}
                <Typography.Title level={5}>輸出結果</Typography.Title>
                <Typography.Paragraph>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {logItem.output ?? '（無）'}
                  </pre>
                </Typography.Paragraph>

                {/* System Prompt */}
                <Typography.Title level={5}>System Prompt</Typography.Title>
                <Typography.Paragraph type="secondary">
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {logItem.systemPrompt ?? '（無）'}
                  </pre>
                </Typography.Paragraph>
              </div>
            ),
          }))}
        />
      )}
    </Drawer>
  );
}
